import { ChevronRight, Clock } from "lucide-react";
import { calcularTurnoDelDia, overrideDelDia } from "../../utils/calendario";
import { trackEvent } from "../../lib/eventTracking";
import TarjetaHoy from "../calendario/TarjetaHoy";
import TiraSemana, { diasDeLaSemana } from "../calendario/TiraSemana";

// Tu semana: la presencia PROTAGONISTA del calendario en el Home minimal
// (dictado del fundador 2026-08-02: "el calendario es gran atractivo, activo
// silencioso, pero está demasiado silencioso"). La tira de 7 días con los
// colores de turno (tokens shift-*, los mismos de la vista mensual y el
// widget Android), el día de hoy destacado y el próximo turno en una línea.
// Tocar un día abre el calendario EN ese día; tocar el resto abre el mes.
//
// Respeta los overrides que el Home ya trae (hoy + 14 días): un día cambiado
// a mano se pinta como quedó, no como dice el patrón (misma regla del barrido
// 2026-08-02: jamás afirmar un turno que el usuario ya cambió). Los días de
// la semana YA pasados se atenúan: la semana se lee como línea de tiempo.

export default function SemanaTurnos({ rotacion, overrides = [], onVerCalendario, proximo = null, ahora = new Date() }) {
  if (!rotacion || !onVerCalendario) return null;
  const dias = diasDeLaSemana(rotacion, overrides, ahora);
  // "Tu turno Nº X del mes" (pedido del fundador 2026-08-02: el "día N de tu
  // rotación" no se veía bien): cuenta los turnos del mes hasta hoy inclusive,
  // con override donde lo tenemos (desde el lunes; los días previos van por el
  // patrón, best-effort). Solo se muestra si HOY es turno: es el dato del día,
  // no un contador permanente.
  const turnoDelMes = (() => {
    const turnoHoy = dias.find((d) => d.esHoy)?.turno;
    if (!turnoHoy || turnoHoy.tipo === "libre") return null;
    let n = 0;
    for (let i = 1; i <= ahora.getDate(); i++) {
      const f = new Date(ahora.getFullYear(), ahora.getMonth(), i);
      const t = calcularTurnoDelDia(f, rotacion, overrideDelDia(f, overrides));
      if (t && t.tipo !== "libre") n += 1;
    }
    return n;
  })();

  const abrir = (fecha = null) => {
    trackEvent("home_semana_tap", { modulo: "calendario", payload: { destino: fecha ? "dia" : "mes" } });
    onVerCalendario(fecha || undefined);
  };

  // El turno de HOY con su override aplicado: la tarjeta de arriba lo lee de la
  // misma lista que pinta la semana, así jamás pueden decir cosas distintas.
  const turnoHoy = dias.find((d) => d.esHoy)?.turno || null;

  return (
    <div className="flex flex-col gap-3">
    {/* "Ese aire de CALENDARIO" (dictado del fundador 2026-08-09): la fecha de
        hoy en grande encabeza la semana, igual que en el riel de MiPAE en turno.
        Es LA MISMA pieza (calendario/TarjetaHoy.jsx), no una copia parecida. */}
    <TarjetaHoy turnoHoy={turnoHoy} ahora={ahora} />
    <section className="rounded-tk-lg border border-edge bg-paper p-4 shadow-tk-sm" aria-label="Tu semana de turnos">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-micro text-ink-muted font-sans uppercase tracking-wide">Tu semana</p>
        <button
          type="button"
          onClick={() => abrir()}
          className="inline-flex items-center gap-0.5 text-small text-accent hover:text-accent-hover font-semibold font-sans transition-colors"
        >
          Ver mes <ChevronRight size={13} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>
      {turnoDelMes && (
        <p className="mt-0.5 text-micro text-ink-faint font-sans">Tu turno Nº {turnoDelMes} del mes</p>
      )}

      {/* Aquí cada día SÍ es tocable: el Home sabe abrir el calendario en la
          fecha exacta (onVerCalendario recibe la fecha). */}
      <TiraSemana dias={dias} onDia={(fecha) => abrir(fecha)} className="mt-3" />

      {proximo && (
        <button
          type="button"
          onClick={() => abrir()}
          className="mt-3 w-full min-h-[36px] flex items-center gap-2 rounded-tk-sm bg-surface px-3 py-2 text-left hover:bg-accent-bg/40 transition-colors"
        >
          <Clock size={14} strokeWidth={1.8} className="text-module-prepara-turno flex-shrink-0" aria-hidden="true" />
          <span className="flex-1 min-w-0 text-small text-ink font-sans truncate">
            Próximo turno: <span className="font-semibold">{proximo.dia}</span> · {proximo.tipo} {proximo.horario}
          </span>
        </button>
      )}
    </section>
    </div>
  );
}
